import React, { useEffect, useState, useCallback } from 'react';
import {
    Typography,
    Button,
    Stack,
    TextField,
    Alert,
    List,
    ListItem,
    ListItemText,
    Paper,
    Box,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControlLabel,
    Checkbox,
    Switch,
    FormGroup,
    FormHelperText,
    MenuItem,
    Divider,
    IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import client from '../api/client';
import { useAuth } from '../context/AuthContext';

const emptyForm = {
    years: '',
    visible: true,
    teamIds: [],
    leagueMode: 'auto',
    rounds: 2,
    cupType: 'none',
    cupGroups: 2,
    cupAdvance: 2,
    cupGroupRounds: 1,
    drawTeams: false,
    rebuildLeague: true,
    rebuildCup: true,
};

const AdminSeasons = () => {
    const { isSuperAdmin } = useAuth();
    const [seasons, setSeasons] = useState([]);
    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editId, setEditId] = useState(null); // null = create
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [seasonsRes, teamsRes] = await Promise.all([
                client.get('/seasons'),
                client.get('/teams/all'),
            ]);
            setSeasons(seasonsRes.data);
            setTeams(teamsRes.data);
        } catch {
            setError('Nie udało się pobrać danych.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

    const toggleTeam = (id) =>
        setForm((f) => ({
            ...f,
            teamIds: f.teamIds.includes(id)
                ? f.teamIds.filter((t) => t !== id)
                : [...f.teamIds, id],
        }));

    const openCreate = () => {
        setForm(emptyForm);
        setEditId(null);
        setError(null);
        setDialogOpen(true);
    };

    const openEdit = async (season) => {
        setError(null);
        try {
            const res = await client.get(`/seasons/${season.id}`);
            setForm({
                ...emptyForm,
                years: res.data.years,
                visible: res.data.visible,
                teamIds: res.data.team_ids || [],
            });
            setEditId(season.id);
            setDialogOpen(true);
        } catch {
            setError('Nie udało się wczytać sezonu.');
        }
    };

    const buildPayload = () => ({
        years: form.years.trim(),
        visible: form.visible,
        team_ids: form.teamIds,
        league: { mode: form.leagueMode, rounds: Number(form.rounds) || 2 },
        cup: {
            type: form.cupType,
            groups: Number(form.cupGroups) || 2,
            advancePerGroup: Number(form.cupAdvance) || 2,
            groupRounds: Number(form.cupGroupRounds) || 1,
            drawTeams: form.drawTeams,
        },
        rebuildLeague: form.rebuildLeague,
        rebuildCup: form.rebuildCup,
    });

    const describeResult = (data) => {
        const parts = [];
        if (data.league?.matches) {
            parts.push(`${data.league.gameweeks} kolejek / ${data.league.matches} meczów ligowych`);
        }
        if (data.cup?.groupMatches) parts.push(`${data.cup.groupMatches} meczów grupowych`);
        if (data.cup?.knockoutMatches) parts.push(`${data.cup.knockoutMatches} meczów pucharowych`);
        return parts.length ? ` Utworzono: ${parts.join(', ')}.` : '';
    };

    const submit = async () => {
        setSaving(true);
        setError(null);
        try {
            const payload = buildPayload();
            const res = editId
                ? await client.post(`/seasons/${editId}/regenerate`, payload)
                : await client.post('/seasons', payload);
            setDialogOpen(false);
            setMessage(
                (editId ? 'Zaktualizowano sezon.' : 'Dodano sezon.') + describeResult(res.data)
            );
            load();
        } catch (err) {
            setError(
                err.response?.data?.error ||
                    'Nie udało się zapisać sezonu. Sprawdź format (RRRR-RRRR).'
            );
        } finally {
            setSaving(false);
        }
    };

    const toggleVisibility = async (season) => {
        try {
            await client.put(`/seasons/${season.id}`, { visible: !season.visible });
            setSeasons((prev) =>
                prev.map((s) => (s.id === season.id ? { ...s, visible: !s.visible } : s))
            );
        } catch {
            setError('Nie udało się zmienić widoczności sezonu.');
        }
    };

    const deleteSeason = async () => {
        try {
            await client.delete(`/seasons/${confirmDelete.id}`);
            setConfirmDelete(null);
            setMessage('Usunięto sezon.');
            load();
        } catch {
            setError('Nie udało się usunąć sezonu.');
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    const yearsValid = /^\d{4}-\d{4}$/.test(form.years.trim());

    return (
        <>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h4">Sezony</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                    Dodaj sezon
                </Button>
            </Stack>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}
            {message && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage(null)}>
                    {message}
                </Alert>
            )}

            <Paper sx={{ p: 2, maxWidth: 640 }}>
                <List>
                    {seasons.map((s) => (
                        <ListItem
                            key={s.id}
                            secondaryAction={
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <FormControlLabel
                                        sx={{ mr: 0 }}
                                        control={
                                            <Switch
                                                edge="end"
                                                checked={!!s.visible}
                                                onChange={() => toggleVisibility(s)}
                                            />
                                        }
                                        label="Widoczny"
                                        labelPlacement="start"
                                    />
                                    <IconButton onClick={() => openEdit(s)} aria-label="Edytuj">
                                        <EditIcon />
                                    </IconButton>
                                    {isSuperAdmin && (
                                        <IconButton
                                            color="error"
                                            onClick={() => setConfirmDelete(s)}
                                            aria-label="Usuń"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    )}
                                </Stack>
                            }
                        >
                            <ListItemText
                                primary={s.years}
                                secondary={s.visible ? 'Publiczny' : 'Ukryty dla gości'}
                            />
                        </ListItem>
                    ))}
                    {seasons.length === 0 && (
                        <Typography color="text.secondary">Brak sezonów.</Typography>
                    )}
                </List>
            </Paper>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{editId ? 'Edytuj sezon' : 'Nowy sezon'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Sezon (RRRR-RRRR)"
                            value={form.years}
                            onChange={(e) => setField('years', e.target.value)}
                            placeholder="2026-2027"
                            required
                            fullWidth
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={form.visible}
                                    onChange={(e) => setField('visible', e.target.checked)}
                                />
                            }
                            label="Widoczny dla niezalogowanych"
                        />

                        {editId && (
                            <>
                                <Alert severity="warning">
                                    Zapis usunie i utworzy od nowa zaznaczone części. Zwykły admin nie
                                    może przebudować części, w której są już wyniki.
                                </Alert>
                                <FormGroup>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={form.rebuildLeague}
                                                onChange={(e) =>
                                                    setField('rebuildLeague', e.target.checked)
                                                }
                                            />
                                        }
                                        label="Przebuduj ligę"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={form.rebuildCup}
                                                onChange={(e) =>
                                                    setField('rebuildCup', e.target.checked)
                                                }
                                            />
                                        }
                                        label="Przebuduj puchar"
                                    />
                                </FormGroup>
                            </>
                        )}

                        <Divider textAlign="left">Drużyny</Divider>
                        <FormGroup>
                            {teams.map((t) => (
                                <FormControlLabel
                                    key={t.id}
                                    control={
                                        <Checkbox
                                            checked={form.teamIds.includes(t.id)}
                                            onChange={() => toggleTeam(t.id)}
                                        />
                                    }
                                    label={t.name}
                                />
                            ))}
                            {teams.length === 0 && (
                                <FormHelperText>Najpierw dodaj drużyny w sekcji „Drużyny".</FormHelperText>
                            )}
                        </FormGroup>
                        <FormHelperText>Wybrano {form.teamIds.length} drużyn.</FormHelperText>

                        <Divider textAlign="left">Liga</Divider>
                        <TextField
                            select
                            label="Terminarz ligowy"
                            value={form.leagueMode}
                            onChange={(e) => setField('leagueMode', e.target.value)}
                            fullWidth
                        >
                            <MenuItem value="auto">Wygeneruj automatycznie</MenuItem>
                            <MenuItem value="empty">Puste mecze (uzupełnię ręcznie)</MenuItem>
                            <MenuItem value="none">Brak ligi</MenuItem>
                        </TextField>
                        {form.leagueMode !== 'none' && (
                            <TextField
                                type="number"
                                label="Liczba meczów między każdą parą"
                                value={form.rounds}
                                onChange={(e) => setField('rounds', e.target.value)}
                                inputProps={{ min: 1, max: 4 }}
                                helperText="Domyślnie 2 (mecz i rewanż)."
                                fullWidth
                            />
                        )}

                        <Divider textAlign="left">Puchar</Divider>
                        <TextField
                            select
                            label="Format pucharu"
                            value={form.cupType}
                            onChange={(e) => setField('cupType', e.target.value)}
                            fullWidth
                        >
                            <MenuItem value="none">Brak pucharu</MenuItem>
                            <MenuItem value="knockout">Tylko pucharowy (knockout)</MenuItem>
                            <MenuItem value="groups_knockout">Grupy + knockout</MenuItem>
                        </TextField>
                        {form.cupType === 'groups_knockout' && (
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <TextField
                                    type="number"
                                    label="Liczba grup"
                                    value={form.cupGroups}
                                    onChange={(e) => setField('cupGroups', e.target.value)}
                                    inputProps={{ min: 1, max: 8 }}
                                    fullWidth
                                />
                                <TextField
                                    type="number"
                                    label="Awansuje z grupy"
                                    value={form.cupAdvance}
                                    onChange={(e) => setField('cupAdvance', e.target.value)}
                                    inputProps={{ min: 1, max: 4 }}
                                    fullWidth
                                />
                                <TextField
                                    type="number"
                                    label="Mecze w grupie (rundy)"
                                    value={form.cupGroupRounds}
                                    onChange={(e) => setField('cupGroupRounds', e.target.value)}
                                    inputProps={{ min: 1, max: 2 }}
                                    fullWidth
                                />
                            </Stack>
                        )}
                        {form.cupType === 'knockout' && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={form.drawTeams}
                                        onChange={(e) => setField('drawTeams', e.target.checked)}
                                    />
                                }
                                label="Rozlosuj drużyny do drabinki od razu"
                            />
                        )}
                        {form.cupType !== 'none' && (
                            <FormHelperText>
                                {form.cupType === 'knockout' && form.drawTeams
                                    ? 'Pierwsza runda zostanie rozlosowana; kolejne rundy uzupełnisz w miarę rozgrywek.'
                                    : form.cupType === 'groups_knockout'
                                      ? 'Drużyny zostaną rozlosowane do grup; drabinkę uzupełnisz po fazie grupowej.'
                                      : 'Drabinka pucharowa powstaje pusta — drużyny uzupełnisz w miarę rozgrywek.'}
                            </FormHelperText>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Anuluj</Button>
                    <Button variant="contained" onClick={submit} disabled={saving || !yearsValid}>
                        {saving ? 'Zapisywanie…' : editId ? 'Zapisz i przebuduj' : 'Utwórz sezon'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
                <DialogTitle>Usunąć sezon?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Czy na pewno chcesz usunąć sezon „{confirmDelete?.years}" wraz ze wszystkimi
                        meczami, kolejkami i wynikami? Tej operacji nie można cofnąć.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDelete(null)}>Anuluj</Button>
                    <Button color="error" variant="contained" onClick={deleteSeason}>
                        Usuń
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default AdminSeasons;
